import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { removeEmpty } from '../../../service/table.service';
import type { AdminClient } from '../admin-clients/adminClientSlice';

const { logError } = RaygunErrorHandlerService();

type InActiveClientSliceType = {
  inActiveClientData: AdminClient[];
  requestInActiveClientParam: RequestParams<AdminClient>;
  loadingInActiveClients: boolean;
};

const initialState: InActiveClientSliceType = {
  inActiveClientData: [],
  requestInActiveClientParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminClient>('inactive_days', 'descend'),
    filteredInfo: {},
  },
  loadingInActiveClients: false,
};

export const fetchInActiveClients = createAsyncThunk<AdminClient[], undefined, AsyncThunkConfig>(
  'inActive/fetchInActiveClients',
  async (_, thunkAPI) => {
    try {
      const params = thunkAPI.getState().inActiveSlice.requestInActiveClientParam;
      const response = (await axios.post('v3_inactive_clients', params, {
        signal: thunkAPI.signal,
      })) as AdminClient[];
      return response ?? [];
    } catch (e) {
      logError(e, ['inActiveSlice', 'fetchInActiveClients']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const inActiveSlice = createSlice({
  name: 'inActive',
  initialState,
  reducers: {
    handleInActiveClientTableChange: (state, action: PayloadAction<TableChange<AdminClient>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminClient>;
      if (!sorter.order) {
        sorter.order = null;
      }
      state.requestInActiveClientParam.pagination = action.payload.pagination;
      state.requestInActiveClientParam.sortedInfo = sorter;
      state.requestInActiveClientParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestInActiveClientParam.pagination = initialState.requestInActiveClientParam.pagination;
      state.requestInActiveClientParam.sortedInfo = initialState.requestInActiveClientParam.sortedInfo;
      state.requestInActiveClientParam.filteredInfo = initialState.requestInActiveClientParam.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInActiveClients.pending, (state, _action) => {
        state.loadingInActiveClients = true;
      })
      .addCase(fetchInActiveClients.fulfilled, (state, action) => {
        state.inActiveClientData = action.payload;
        state.loadingInActiveClients = false;
      })
      .addCase(fetchInActiveClients.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingInActiveClients = false;
        }
      });
  },
});

export const { handleInActiveClientTableChange, clearFilter } = inActiveSlice.actions;
